import { useCallback, useEffect, useState } from 'react'
import { useAccount } from 'wagmi'

import abiPTODistributionV3 from 'abi/abi-pto-distribution-v3.json'
import abiPTOOrchestrator from 'abi/abi-pto-orchestrator.json'
import { SettingEnum } from 'constants/setting'
import { useContractViem } from 'hooks/contract/use-contract-viem'
import { useAppSetting } from 'hooks/setting/use-app-setting'
import { logError } from 'utils/logger'

/**
 * Orchestrator ABI plus the error entries of PTODistribution: createDistributionPool
 * bubbles Dist_* reverts up through the orchestrator, and viem can only name them when
 * they are part of the ABI used for the call.
 */
const ORCHESTRATOR_ABI_WITH_NESTED_ERRORS = [
  ...(abiPTOOrchestrator as any[]),
  ...(abiPTODistributionV3 as any[]).filter(
    (entry) =>
      entry.type === 'error' &&
      !(abiPTOOrchestrator as any[]).some((o) => o.type === 'error' && o.name === entry.name)
  ),
]

export const ORCHESTRATOR_NOT_CONFIGURED = `PTO Orchestrator address is not configured - add the setting "${SettingEnum.PTO_ORCHESTRATOR_ADDRESS}" on the Setting page`

/**
 * PTOOrchestrator (ERC-3643) contract instance + whether the connected wallet holds
 * its agent role (createPTO / createDistributionPool are onlyAgent).
 *
 * `isAgent === null` means unknown (no orchestrator address configured, or the read
 * failed) - callers should not block on it, the simulation reverts with
 * AgentRole_UnauthorizedAgent before the wallet is asked to sign anyway.
 */
export const usePtoOrchestrator = () => {
  const { getSetting } = useAppSetting()
  const { address } = useAccount()

  const orchestratorAddress = getSetting(SettingEnum.PTO_ORCHESTRATOR_ADDRESS)
  const contract = useContractViem(orchestratorAddress, ORCHESTRATOR_ABI_WITH_NESTED_ERRORS)

  const [isAgent, setIsAgent] = useState<boolean | null>(null)

  const checkAgent = useCallback(async () => {
    if (!contract || !address) {
      setIsAgent(null)
      return
    }
    try {
      const res = await contract.read.isAgent([address])
      setIsAgent(Boolean(res))
    } catch (error) {
      setIsAgent(null)
      logError('PTOOrchestrator.isAgent check failed', error)
    }
  }, [address, contract])

  useEffect(() => {
    checkAgent()
  }, [checkAgent])

  return { orchestratorAddress, contract, isAgent, checkAgent }
}
